import { useTranslation } from 'react-i18next';
import { Home, Users, Phone, Settings } from 'lucide-react';
import { VariableSection } from '../utils/variableTypes';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';

interface SetupIntroductionProps {
    sections: VariableSection[];
    onStart: () => void;
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
    mriSafetyLeadership: <Users className="h-4 w-4" />,
    keyOperationalContacts: <Phone className="h-4 w-4" />,
    departmentInformation: <Home className="h-4 w-4" />,
    optionalOperationalFields: <Settings className="h-4 w-4" />,
};

const SECTION_DESCRIPTION_KEYS: Record<string, string> = {
    mriSafetyLeadership: 'departmentVariables.setupIntro.sections.mriSafetyLeadership',
    keyOperationalContacts: 'departmentVariables.setupIntro.sections.keyOperationalContacts',
    departmentInformation: 'departmentVariables.setupIntro.sections.departmentInformation',
    optionalOperationalFields: 'departmentVariables.setupIntro.sections.optionalOperationalFields',
};

export default function SetupIntroduction({
    sections,
    onStart,
}: SetupIntroductionProps) {
    const { t } = useTranslation();

    return (
        <Card className="border-subtle bg-card">
            <CardContent className="px-6 py-6">
                <div className="space-y-3">
                    <h2 className="text-lg font-semibold">{t('departmentVariables.setupIntro.title')}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        {t('departmentVariables.setupIntro.description')}
                    </p>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {sections.map((section) => (
                        <div
                            key={section.key}
                            className="rounded-md border border-subtle bg-background/30 px-3 py-3"
                        >
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 text-muted-foreground">
                                    {SECTION_ICONS[section.key] ?? (
                                        <Home className="h-4 w-4" />
                                    )}
                                </span>
                                <div>
                                    <p className="text-sm font-medium text-foreground">
                                        {section.title}
                                    </p>
                                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                                        {SECTION_DESCRIPTION_KEYS[section.key]
                                            ? t(SECTION_DESCRIPTION_KEYS[section.key])
                                            : section.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-5 flex justify-end">
                    <Button type="button" onClick={onStart}>
                        {t('departmentVariables.setupIntro.startSetup')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
